import React, { useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { initAnalytics, trackPageView } from "./utils/analytics";

// Components
import Header from "./components/Header";
import HeroSection from "./components/HeroSection";
import PreviewSection from "./components/PreviewSection";
import SyllabusSection from "./components/SyllabusSection";
import CountdownSection from "./components/CountdownSection";
import FAQSection from "./components/FAQSection";
import TestimonialsSection from "./components/TestimonialsSection";
import NewsletterSection from "./components/NewsletterSection";
import Chatbot from "./components/Chatbot";
import Footer from "./components/Footer";
import AuthCallback from "./components/AuthCallback";

// Pages
import SyllabusPage from "./pages/SyllabusPage";
import InstructorsPage from "./pages/InstructorsPage";
import PricingPage from "./pages/PricingPage";
import CheckoutSuccessPage from "./pages/CheckoutSuccessPage";
import AboutPage from "./pages/AboutPage";
import ContactPage from "./pages/ContactPage";
import PreviewPage from "./pages/PreviewPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import TermsOfServicePage from "./pages/TermsOfServicePage";
import RefundPolicyPage from "./pages/RefundPolicyPage";
import CareersPage from "./pages/CareersPage";
import BlogPage from "./pages/BlogPage";
import AdminVideosPage from "./pages/AdminVideosPage";

// Analytics wrapper
const AnalyticsWrapper = ({ children }) => {
  const location = useLocation();

  useEffect(() => {
    trackPageView();
  }, [location.pathname]);

  return children;
};

const LandingPage = () => {
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleEnrollClick = () => {
    window.location.href = '/pricing';
  };

  const handlePreviewClick = () => {
    window.location.href = '/preview';
  };

  return (
    <div className="landing-page">
      <Header onEnrollClick={handleEnrollClick} />
      <main>
        <HeroSection 
          onEnrollClick={handleEnrollClick} 
          onPreviewClick={handlePreviewClick}
        />
        <PreviewSection />
        <SyllabusSection />
        <TestimonialsSection />
        <section id="countdown">
          <CountdownSection onEnrollClick={handleEnrollClick} />
        </section>
        <FAQSection />
        <section id="newsletter">
          <NewsletterSection />
        </section>
      </main>
      <Footer />
      <Chatbot />
    </div>
  );
};

// Router component to handle auth callback
const AppRouter = () => {
  const location = useLocation();

  // Check URL fragment for session_id (auth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <AnalyticsWrapper>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/syllabus" element={<SyllabusPage />} />
        <Route path="/instructors" element={<InstructorsPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/preview" element={<PreviewPage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/terms-of-service" element={<TermsOfServicePage />} />
        <Route path="/refund-policy" element={<RefundPolicyPage />} />
        <Route path="/careers" element={<CareersPage />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/admin/videos" element={<AdminVideosPage />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
      </Routes>
      <Chatbot />
    </AnalyticsWrapper>
  );
};

function App() {
  useEffect(() => {
    initAnalytics();
  }, []);

  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
